// src/native/compliance/AiConsentScreen.jsx
//
// WS-7 COMPLIANCE. App Store Guideline 5.1.2(i), updated 2025-11-13:
// "You must clearly disclose where personal data will be shared with third
// parties, including with third-party AI, and obtain explicit permission before
// doing so." A privacy policy link is explicitly not sufficient, so every
// company is named here, on screen, before anything is sent.
//
// This screen also runs on the web (see AI_CONSENT_GATE_SCOPE), which is why it
// is ONE screen with ONE primary button. The minors affirmation required by
// 5.1.4(b) and GDPR Article 8 sits in the sentence above that button rather
// than in a step of its own.
//
// Copy rules, which are not negotiable:
//   - The readers are parents of children with a speech delay, and sometimes the
//     children themselves. Short sentences, ordinary words, no legalese.
//   - No pre-ticked box and no implied consent on continue. The user taps
//     "I agree" or they do not.
//   - Saying no is a real, calm option that does not scold anybody.
//
// Performance note: everything here is static markup and local state. There is
// no data fetch, no image, and no measured layout, so it paints in one frame and
// cannot shift.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { pageContentSafeAreaStyle } from '@/lib/safeArea';
import { PRIVACY_ROUTE, SUPPORT_EMAIL } from './consentConfig';
import ProcessorTable from './ProcessorTable';
import MinorsAffirmation from './MinorsAffirmation';

export default function AiConsentScreen({
  t,
  lightMode,
  reducedMotion,
  onAgree,
  onDecline,
  onDismiss,
  onUnderAge,
}) {
  const [declined, setDeclined] = useState(false);
  const [underAge, setUnderAge] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleAgree = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onAgree();
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onDecline();
      setDeclined(true);
    } finally {
      setBusy(false);
    }
  };

  const handleUnderAge = () => {
    setUnderAge(true);
    onUnderAge?.();
  };

  const surface = lightMode ? 'bg-[#FFFBF7]' : 'bg-[#161628]';
  const heading = lightMode ? 'text-gray-900' : 'text-white';
  const bodyText = lightMode ? 'text-gray-600' : 'text-white/70';

  const primaryButtonStyle = {
    background: 'linear-gradient(135deg, #E07A4D 0%, #C8572A 100%)',
    boxShadow: '0 6px 16px -4px rgba(200, 87, 42, 0.5)',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('native.consent.title')}
      className={cn(
        'fixed inset-0 z-[130] overflow-y-auto overscroll-contain',
        surface,
        // Opaque and full bleed on purpose. A translucent sheet over a half
        // visible app reads as dismissible, and this one is not.
        // The fade is skipped under reduced motion, which defaults to on here.
        reducedMotion ? '' : 'animate-sf-fade-in'
      )}
      style={{ minHeight: '100svh' }}
    >
      <div className="mx-auto w-full max-w-md px-5 pb-10" style={pageContentSafeAreaStyle}>
        {declined ? (
          <div className="flex flex-col gap-5 pt-6">
            <h1 className={cn('text-[20px] font-bold leading-tight', heading)}>
              {t('native.consent.declinedTitle')}
            </h1>
            <p className={cn('text-[14px] leading-relaxed', bodyText)}>
              {t('native.consent.declinedBody')}
            </p>

            <button
              type="button"
              onClick={handleAgree}
              disabled={busy}
              className="w-full min-h-[56px] rounded-2xl py-3.5 text-[16px] font-semibold text-white touch-manipulation transition-transform active:scale-[0.98] disabled:opacity-60"
              style={primaryButtonStyle}
            >
              {t('native.consent.declinedAgree')}
            </button>

            <button
              type="button"
              onClick={onDismiss}
              disabled={busy}
              className={cn(
                'w-full min-h-[56px] rounded-2xl py-3.5 text-[15px] font-medium touch-manipulation disabled:opacity-60',
                lightMode ? 'text-gray-500' : 'text-white/60'
              )}
            >
              {t('native.consent.declinedContinue')}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <div className="flex flex-col items-center text-center">
              <span
                className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl shadow-lg"
                style={{
                  background: 'linear-gradient(135deg, #E07A4D 0%, #C8572A 100%)',
                  boxShadow: '0 10px 24px -8px rgba(200, 87, 42, 0.45)',
                }}
                aria-hidden="true"
              >
                <ShieldCheck weight="fill" className="h-8 w-8 text-white" />
              </span>

              <h1 className={cn('text-[22px] font-bold leading-tight', heading)}>
                {t('native.consent.title')}
              </h1>
              <p className={cn('mt-2 text-[14px] leading-relaxed', bodyText)}>
                {t('native.consent.intro')}
              </p>
            </div>

            <ProcessorTable t={t} lightMode={lightMode} />

            <p
              className={cn(
                'text-[13px] leading-relaxed',
                lightMode ? 'text-gray-600' : 'text-white/65'
              )}
            >
              {t('native.consent.promise')}
            </p>

            <MinorsAffirmation
              t={t}
              lightMode={lightMode}
              underAge={underAge}
              onDeclareUnderAge={handleUnderAge}
            />

            <div className="flex flex-col gap-2.5">
              <button
                type="button"
                onClick={handleAgree}
                disabled={busy}
                className="w-full min-h-[56px] rounded-2xl py-3.5 text-[16px] font-semibold text-white touch-manipulation transition-transform active:scale-[0.98] disabled:opacity-60"
                style={primaryButtonStyle}
              >
                {t('native.consent.agree')}
              </button>

              <button
                type="button"
                onClick={handleDecline}
                disabled={busy}
                className={cn(
                  'w-full min-h-[56px] rounded-2xl py-3.5 text-[15px] font-medium touch-manipulation disabled:opacity-60',
                  lightMode ? 'text-gray-500' : 'text-white/60'
                )}
              >
                {t('native.consent.notNow')}
              </button>
            </div>

            <div className="flex flex-col items-center gap-1.5 pt-1">
              <Link
                to={PRIVACY_ROUTE}
                className="min-h-[44px] flex items-center text-[13px] font-medium underline underline-offset-2"
                style={{ color: '#C8572A' }}
              >
                {t('native.consent.privacyLink')}
              </Link>
              <p className={cn('text-[12.5px] text-center', lightMode ? 'text-gray-500' : 'text-white/50')}>
                {t('native.consent.contact')}{' '}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="underline underline-offset-2"
                  style={{ color: '#C8572A' }}
                >
                  {SUPPORT_EMAIL}
                </a>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
